import { View, Text, StyleSheet, Font } from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { PdfLayout } from "../pdf/PdfLayout";
import { commonStyles } from "../pdf/CommonPdfStyles";
import MarkdownRenderer from "../../../tools/components/custom-markdown";
import { PdfCustomizationSettings } from "../../../common/components/pdf-customization-modal";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";
import {
  ChoiceBoardResponseDto,
  ActivityItem,
} from "@packages/api/madlenAPI.schemas";
import { tC } from "@features/tools/utils/TranslateWithContentLanguage";

Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

interface ChoiceBoardCardProps {
  choiceBoard: ChoiceBoardResponseDto;
  customizationSettings: PdfCustomizationSettings;
}

// PDF Difficulty Distribution Component
const PdfDifficultyDistribution = ({
  distributionString,
  customizationSettings,
  contentLanguage,
}: {
  distributionString: string;
  customizationSettings: PdfCustomizationSettings;
  contentLanguage: string;
}) => {
  let distribution: Record<string, number> = {};
  try {
    distribution = JSON.parse(distributionString);
  } catch (error) {
    distribution = { easy: 33, medium: 33, hard: 34 };
  }

  const total = Object.values(distribution).reduce(
    (sum, value) => sum + value,
    0,
  );

  const difficultyConfig = {
    easy: { color: "#16a34a", bgColor: "#dcfce7", textColor: "#166534" },
    medium: { color: "#ca8a04", bgColor: "#fef3c7", textColor: "#92400e" },
    hard: { color: "#dc2626", bgColor: "#fecaca", textColor: "#991b1b" },
  };

  return (
    <View style={styles.difficultyContainer}>
      <Text
        style={[
          styles.difficultyTitle,
          {
            fontSize: scaleFontSize(
              styles.difficultyTitle.fontSize,
              customizationSettings?.fontSize,
            ),
          },
        ]}
      >
        {tC(contentLanguage, "choiceBoards.difficultyDistribution")}
      </Text>

      {/* Progress Bars */}
      <View style={styles.difficultyBars}>
        {Object.entries(distribution).map(([level, value]) => {
          const percentage = total > 0 ? (value / total) * 100 : 0;
          const config =
            difficultyConfig[
              level.toLowerCase() as keyof typeof difficultyConfig
            ] || difficultyConfig.medium;

          return (
            <View key={level} style={styles.difficultyBarContainer}>
              <View style={styles.difficultyBarHeader}>
                <Text
                  style={[
                    styles.difficultyLabel,
                    {
                      fontSize: scaleFontSize(
                        styles.difficultyLabel.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  {tC(
                    contentLanguage,
                    `choiceBoards.difficulty.${level.toLowerCase()}`,
                  )}
                </Text>
                <Text
                  style={[
                    styles.difficultyPercentage,
                    {
                      fontSize: scaleFontSize(
                        styles.difficultyPercentage.fontSize,
                        customizationSettings?.fontSize,
                      ),
                      color: config.textColor,
                    },
                  ]}
                >
                  {percentage.toFixed(0)}%
                </Text>
              </View>
              <View style={styles.difficultyBarBackground}>
                <View
                  style={[
                    styles.difficultyBarFill,
                    {
                      backgroundColor: config.color,
                      width: `${percentage}%`,
                    },
                  ]}
                />
              </View>
            </View>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  // Instructions section
  instructionsContainer: {
    marginBottom: 20,
    padding: 16,
    backgroundColor: "#eff6ff",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#3b82f6",
  },
  instructionsTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#1d4ed8",
    marginBottom: 10,
  },

  // Advanced settings section
  advancedContainer: {
    marginBottom: 20,
    padding: 16,
    backgroundColor: "#f3e8ff",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#8b5cf6",
  },
  advancedTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#7c3aed",
    marginBottom: 14,
  },
  settingsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  settingItem: {
    width: "48%",
    marginBottom: 12,
  },
  settingItemFull: {
    width: "100%",
    marginBottom: 12,
  },
  settingLabel: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#374151",
    marginBottom: 6,
  },
  settingValue: {
    fontSize: 10,
    color: "#6b7280",
    backgroundColor: "#f9fafb",
    padding: 8,
    borderRadius: 6,
    lineHeight: 1.4,
  },
  settingTags: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
  },
  settingTag: {
    fontSize: 9,
    backgroundColor: "#dbeafe",
    color: "#1e40af",
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
    marginRight: 4,
    marginBottom: 4,
  },

  // Difficulty Distribution styles
  difficultyContainer: {
    marginBottom: 16,
    padding: 12,
    backgroundColor: "#f8fafc",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e2e8f0",
  },
  difficultyTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#4f46e5",
    marginBottom: 10,
  },
  difficultyBars: {
    marginBottom: 12,
  },
  difficultyBarContainer: {
    marginBottom: 8,
  },
  difficultyBarHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 3,
  },
  difficultyLabel: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#374151",
  },
  difficultyPercentage: {
    fontSize: 9,
    fontWeight: "bold",
  },
  difficultyBarBackground: {
    height: 8,
    backgroundColor: "#e5e7eb",
    borderRadius: 4,
    overflow: "hidden",
  },
  difficultyBarFill: {
    height: "100%",
    borderRadius: 4,
  },
  difficultySummary: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 6,
  },
  difficultySummaryCard: {
    flex: 1,
    padding: 8,
    borderRadius: 6,
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  difficultySummaryValue: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 2,
  },
  difficultySummaryLabel: {
    fontSize: 8,
    textAlign: "center",
  },

  // Activities section
  activitiesGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  activityCard: {
    width: "32%",
    marginBottom: 16,
    backgroundColor: "#ffffff",
    borderRadius: 12,
    borderWidth: 2,
    overflow: "hidden",
  },
  activityCardEasy: {
    borderColor: "#16a34a",
    backgroundColor: "#f0fdf4",
  },
  activityCardMedium: {
    borderColor: "#ca8a04",
    backgroundColor: "#fffbeb",
  },
  activityCardHard: {
    borderColor: "#dc2626",
    backgroundColor: "#fef2f2",
  },
  activityCardDefault: {
    borderColor: "#3b82f6",
    backgroundColor: "#eff6ff",
  },
  activityAccent: {
    height: 3,
    width: "100%",
  },
  activityAccentEasy: {
    backgroundColor: "#16a34a",
  },
  activityAccentMedium: {
    backgroundColor: "#ca8a04",
  },
  activityAccentHard: {
    backgroundColor: "#dc2626",
  },
  activityAccentDefault: {
    backgroundColor: "#3b82f6",
  },
  activityContent: {
    padding: 12,
  },
  activityHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 8,
  },
  activityTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#111827",
    flex: 1,
    marginRight: 8,
    lineHeight: 1.3,
  },
  difficultyBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 12,
    fontSize: 8,
    fontWeight: "bold",
  },
  difficultyEasy: {
    backgroundColor: "#dcfce7",
    color: "#166534",
  },
  difficultyMedium: {
    backgroundColor: "#fef3c7",
    color: "#92400e",
  },
  difficultyHard: {
    backgroundColor: "#fecaca",
    color: "#991b1b",
  },
  difficultyDefault: {
    backgroundColor: "#dbeafe",
    color: "#1e40af",
  },

  // Activity content sections
  activitySection: {
    marginBottom: 8,
  },
  activitySectionTitle: {
    fontSize: 9,
    fontWeight: "bold",
    marginBottom: 4,
    flexDirection: "row",
    alignItems: "center",
  },
  learningStyleSection: {
    color: "#6b7280",
  },
  learningOutcomesSection: {
    color: "#8b5cf6",
  },
  skillsSection: {
    color: "#ea580c",
  },
  activityContentText: {
    fontSize: 8,
    lineHeight: 1.4,
    color: "#374151",
  },
  activitySubContent: {
    fontSize: 8,
    lineHeight: 1.3,
    color: "#374151",
    backgroundColor: "#f9fafb",
    padding: 6,
    borderRadius: 4,
  },
});

const ChoiceBoardDocument = ({
  choiceBoard,
  customizationSettings,
}: ChoiceBoardCardProps) => {
  const contentLanguage = choiceBoard.contentLanguage ?? "tr";

  const sectionContentMarkdown = {
    text: {
      text: {
        ...commonStyles.sectionContent,
        fontSize: scaleFontSize(
          commonStyles.sectionContent.fontSize,
          customizationSettings?.fontSize,
        ),
      },
    },
  };

  const activityContentMarkdown = {
    text: {
      text: {
        fontSize: scaleFontSize(
          styles.activityContentText.fontSize,
          customizationSettings?.fontSize,
        ),
        lineHeight: styles.activityContentText.lineHeight,
        color: styles.activityContentText.color,
      },
    },
  };

  const getDifficultyStyle = (level?: string) => {
    if (!level)
      return {
        card: styles.activityCardDefault,
        badge: styles.difficultyDefault,
        accent: styles.activityAccentDefault,
      };
    const normalizedLevel = level.toLowerCase();
    if (
      normalizedLevel.includes("easy") ||
      normalizedLevel.includes("beginner")
    )
      return {
        card: styles.activityCardEasy,
        badge: styles.difficultyEasy,
        accent: styles.activityAccentEasy,
      };
    if (
      normalizedLevel.includes("medium") ||
      normalizedLevel.includes("intermediate")
    )
      return {
        card: styles.activityCardMedium,
        badge: styles.difficultyMedium,
        accent: styles.activityAccentMedium,
      };
    if (
      normalizedLevel.includes("hard") ||
      normalizedLevel.includes("advanced")
    )
      return {
        card: styles.activityCardHard,
        badge: styles.difficultyHard,
        accent: styles.activityAccentHard,
      };
    return {
      card: styles.activityCardDefault,
      badge: styles.difficultyDefault,
      accent: styles.activityAccentDefault,
    };
  };

  return (
    <PdfLayout
      courseName={choiceBoard.courseName || ""}
      gradeLevel={choiceBoard.gradeLevel || 0}
      topic={choiceBoard.title || choiceBoard.prompt || ""}
      imageUrl={choiceBoard.imageUrl}
      headerTextColor="#3b82f6"
      customizationSettings={customizationSettings}
      author={choiceBoard.authorName || ""}
      createdDate={choiceBoard.createdAt || ""}
    >
      {/* Choice Board Instructions */}
      {choiceBoard.choiceBoardInstructions && (
        <View style={styles.instructionsContainer} wrap={false}>
          <Text
            style={[
              styles.instructionsTitle,
              {
                fontSize: scaleFontSize(
                  styles.instructionsTitle.fontSize,
                  customizationSettings?.fontSize,
                ),
              },
            ]}
          >
            {tC(contentLanguage, "choiceBoards.instructions")}
          </Text>
          <MarkdownRenderer
            content={choiceBoard.choiceBoardInstructions}
            customStyle={sectionContentMarkdown}
          />
        </View>
      )}

      {/* Advanced Settings Section */}
      {(choiceBoard.preferredLearningStyles?.length ||
        choiceBoard.difficultyDistribution ||
        choiceBoard.selectionRequirements ||
        choiceBoard.learningOutcomesFocus?.length) && (
        <View style={styles.advancedContainer} wrap={false}>
          <Text
            style={[
              styles.advancedTitle,
              {
                fontSize: scaleFontSize(
                  styles.advancedTitle.fontSize,
                  customizationSettings?.fontSize,
                ),
              },
            ]}
          >
            {tC(contentLanguage, "choiceBoards.advancedSettings")}
          </Text>

          <View style={styles.settingsGrid}>
            {/* Learning Styles */}
            {choiceBoard.preferredLearningStyles &&
              choiceBoard.preferredLearningStyles.length > 0 && (
                <View style={styles.settingItem}>
                  <Text
                    style={[
                      styles.settingLabel,
                      {
                        fontSize: scaleFontSize(
                          styles.settingLabel.fontSize,
                          customizationSettings?.fontSize,
                        ),
                      },
                    ]}
                  >
                    {tC(contentLanguage, "choiceBoards.assessmentCriteria")}
                  </Text>
                  <View style={styles.settingTags}>
                    {choiceBoard.preferredLearningStyles.map((style, index) => (
                      <Text
                        key={index}
                        style={[
                          styles.settingTag,
                          {
                            fontSize: scaleFontSize(
                              styles.settingTag.fontSize,
                              customizationSettings?.fontSize,
                            ),
                          },
                        ]}
                      >
                        {style}
                      </Text>
                    ))}
                  </View>
                </View>
              )}

            {/* Learning Outcomes Focus */}
            {choiceBoard.learningOutcomesFocus &&
              choiceBoard.learningOutcomesFocus.length > 0 && (
                <View style={styles.settingItem}>
                  <Text
                    style={[
                      styles.settingLabel,
                      {
                        fontSize: scaleFontSize(
                          styles.settingLabel.fontSize,
                          customizationSettings?.fontSize,
                        ),
                      },
                    ]}
                  >
                    {tC(
                      contentLanguage,
                      "choiceBoards.learningOutcomes.knowledgeRecall",
                    )}
                  </Text>
                  <View style={styles.settingTags}>
                    {choiceBoard.learningOutcomesFocus.map((outcome, index) => (
                      <Text
                        key={index}
                        style={[
                          styles.settingTag,
                          {
                            fontSize: scaleFontSize(
                              styles.settingTag.fontSize,
                              customizationSettings?.fontSize,
                            ),
                            backgroundColor: "#dcfce7",
                            color: "#166534",
                          },
                        ]}
                      >
                        {outcome}
                      </Text>
                    ))}
                  </View>
                </View>
              )}

            {/* Enhanced Difficulty Distribution */}
            {choiceBoard.difficultyDistribution && (
              <View style={styles.settingItemFull}>
                <PdfDifficultyDistribution
                  distributionString={choiceBoard.difficultyDistribution}
                  customizationSettings={customizationSettings}
                  contentLanguage={contentLanguage}
                />
              </View>
            )}

            {/* Selection Requirements */}
            {choiceBoard.selectionRequirements && (
              <View style={styles.settingItemFull}>
                <Text
                  style={[
                    styles.settingLabel,
                    {
                      fontSize: scaleFontSize(
                        styles.settingLabel.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  {tC(contentLanguage, "choiceBoards.assessmentCriteria")}
                </Text>
                <Text
                  style={[
                    styles.settingValue,
                    {
                      fontSize: scaleFontSize(
                        styles.settingValue.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  {choiceBoard.selectionRequirements}
                </Text>
              </View>
            )}
          </View>
        </View>
      )}

      {/* Enhanced Activities Grid */}
      {choiceBoard.activities && choiceBoard.activities.length > 0 && (
        <View style={styles.activitiesGrid}>
          {choiceBoard.activities.map(
            (activity: ActivityItem, index: number) => {
              const difficultyStyles = getDifficultyStyle(
                activity.difficultyLevel,
              );

              return (
                <View
                  key={index}
                  style={[styles.activityCard, difficultyStyles.card]}
                  wrap={false}
                >
                  {/* Accent Bar */}
                  <View
                    style={[styles.activityAccent, difficultyStyles.accent]}
                  />

                  <View style={styles.activityContent}>
                    {/* Activity Header */}
                    <View style={styles.activityHeader}>
                      <Text
                        style={[
                          styles.activityTitle,
                          {
                            fontSize: scaleFontSize(
                              styles.activityTitle.fontSize,
                              customizationSettings?.fontSize,
                            ),
                          },
                        ]}
                      >
                        {activity.title ||
                          `${tC(
                            contentLanguage,
                            "choiceBoards.activity",
                          )} ${index + 1}`}
                      </Text>
                      {activity.difficultyLevel && (
                        <Text
                          style={[
                            styles.difficultyBadge,
                            difficultyStyles.badge,
                            {
                              fontSize: scaleFontSize(
                                styles.difficultyBadge.fontSize,
                                customizationSettings?.fontSize,
                              ),
                            },
                          ]}
                        >
                          {activity.difficultyLevel}
                        </Text>
                      )}
                    </View>

                    {/* Activity Description */}
                    {activity.detail && (
                      <View style={styles.activitySection}>
                        <MarkdownRenderer
                          content={activity.detail}
                          customStyle={activityContentMarkdown}
                        />
                      </View>
                    )}

                    {/* Learning Style */}
                    {activity.learningStyle && (
                      <View style={styles.activitySection}>
                        <Text
                          style={[
                            styles.activitySectionTitle,
                            styles.learningStyleSection,
                            {
                              fontSize: scaleFontSize(
                                styles.activitySectionTitle.fontSize,
                                customizationSettings?.fontSize,
                              ),
                            },
                          ]}
                        >
                          {tC(contentLanguage, "choiceBoards.learningStyle")}:
                        </Text>
                        <Text
                          style={[
                            styles.activitySubContent,
                            {
                              fontSize: scaleFontSize(
                                styles.activitySubContent.fontSize,
                                customizationSettings?.fontSize,
                              ),
                            },
                          ]}
                        >
                          {activity.learningStyle}
                        </Text>
                      </View>
                    )}

                    {/* Learning Outcomes */}
                    {activity.learningOutcomes && (
                      <View style={styles.activitySection}>
                        <Text
                          style={[
                            styles.activitySectionTitle,
                            styles.learningOutcomesSection,
                            {
                              fontSize: scaleFontSize(
                                styles.activitySectionTitle.fontSize,
                                customizationSettings?.fontSize,
                              ),
                            },
                          ]}
                        >
                          {tC(
                            contentLanguage,
                            "choiceBoards.learningOutcomes.knowledgeRecall",
                          )}
                          :
                        </Text>
                        <View style={styles.activitySubContent}>
                          <MarkdownRenderer
                            content={activity.learningOutcomes}
                            customStyle={activityContentMarkdown}
                          />
                        </View>
                      </View>
                    )}

                    {/* Skills Developed */}
                    {activity.skillsDeveloped && (
                      <View style={styles.activitySection}>
                        <Text
                          style={[
                            styles.activitySectionTitle,
                            styles.skillsSection,
                            {
                              fontSize: scaleFontSize(
                                styles.activitySectionTitle.fontSize,
                                customizationSettings?.fontSize,
                              ),
                            },
                          ]}
                        >
                          {tC(contentLanguage, "choiceBoards.skillsDeveloped")}:
                        </Text>
                        <View style={styles.activitySubContent}>
                          <MarkdownRenderer
                            content={activity.skillsDeveloped}
                            customStyle={activityContentMarkdown}
                          />
                        </View>
                      </View>
                    )}
                  </View>
                </View>
              );
            },
          )}
        </View>
      )}
    </PdfLayout>
  );
};

function ChoiceBoardCardPDF(props: ChoiceBoardCardProps) {
  return <ChoiceBoardDocument {...props} />;
}

export default ChoiceBoardCardPDF;
